import { Flex, Text } from '@brokerbay/elements-web';
import { ROLES } from '@bb/users';
import {
  ContactMethodsSelector,
  Input,
  Select,
  SelectOption,
} from 'components';
import type { ContactMethodsSelectorProps } from 'components/ContactMethodsSelector';
import { AuthContext } from 'contexts';
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { correctShowingTypeLabel } from 'utils';
import InviteBuyer from '../../../../../components/InviteBuyer';
import AppointmentBookingInternalContext from '../../../AppointmentBookingInternalContext';
import { agentAppointmentTypes } from '../../constants';
import CoopSelectContext, {
  type CoopSelectContextType,
} from '../CoopSelectContext';
import Notes from './Notes';
import ShowingTypeSelector from './ShowingTypeSelector';

const AgentDetails: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const { listing } = useContext(AppointmentBookingInternalContext);
  const {
    setApptNotify,
    coop,
    setCoop,
    coopAdditionalData,
    buyers,
    setBuyers,
    bookFor,
  } = useContext(CoopSelectContext);

  if (!coop || !coopAdditionalData || coopAdditionalData.coopType !== 'agent')
    return null;
  const { notifications, orgPhone } = coopAdditionalData;
  const showSMSField =
    coopAdditionalData.orgFeatures?.includes('SHOWING_AGENT_SMS') &&
    Boolean(coop.phone);

  const handleNotifyMethod: ContactMethodsSelectorProps['selectNotifyMethod'] =
    method => {
      const notify = { email: false, text: false, office: false };
      const notifyOptions: (keyof typeof notify)[] =
        method === 'email&text' ? ['email', 'text'] : [method];
      notifyOptions.forEach(option => {
        notify[option] = true;
      });
      setApptNotify(notify as CoopSelectContextType['apptNotify']);
    };

  return (
    <StyledFlex vertical gap="small">
      <div>
        <Text>Name: </Text>
        <Text>{coop.name}</Text>
      </div>
      <div>
        <Text size="large">{coopAdditionalData.orgName}</Text>
        <br />
        <Text>{coopAdditionalData.orgAddress}</Text>
      </div>
      {bookFor !== 'self' && (
        <div>
          <Text thickness="bold">Mobile: </Text>
          <Input
            phoneNumberMask
            value={coop.phone}
            onChange={e => {
              const { value } = e.currentTarget;
              setCoop(prev => ({ ...prev!, phone: value }));
            }}
            dataCy="inPersonShowing-edit-mobile"
          />
        </div>
      )}
      <div>
        <Text>Send Instruction By</Text>
        <ContactMethodsSelector
          selectedUser={{
            email: coop.email,
            bookShowingDefault:
              notifications?.officeMessages.bookShowingDefault,
            officePhone: orgPhone,
            phone: coop.phone,
          }}
          selectNotifyMethod={handleNotifyMethod}
          hasSmsFeature={showSMSField}
        />
        <ShowingTypeSelector />
      </div>
      <Notes />
      {(bookFor === 'self' || auth.roles[auth.role] > ROLES.AGENT) &&
        coop.id && (
          <div>
            <Text>Invite Buyers</Text>
            <InviteBuyer
              agent={coop}
              value={buyers}
              onValueChange={setBuyers}
              listing={listing}
            />
          </div>
        )}
    </StyledFlex>
  );
};

const StyledFlex = styled(Flex)`
  padding: 15px 0;
`;

const StyledSelect = styled(Select)`
  margin-bottom: 8px;
  .ant-select > .ant-select-selector {
    background-color: #fff;
    border: 1px solid #e4e5e7;
  }
`;

export default AgentDetails;
